// @givmo/crm — tolerant decode and display helpers for the shared CRM.
//
// ★ NUMBERS-ARE-THE-LAW. Every money figure is the SERVER's string. The decoder accepts only strings,
// preserves their bytes, and no helper parses them into a number or derives a replacement amount.
//
// TRENDS ARE A COMPARISON, NEVER ARITHMETIC. The yearly table marks each year up/down/flat against the
// PREVIOUS year. `compareMoneyStrings` orders two decimal wire strings DIGIT-WISE (sign → integer
// length → integer digits → zero-padded fraction) — it yields an ORDERING, never a value: no
// `parseFloat`, no subtraction, no ratio. There are no percentages anywhere in this feature by design;
// a delta or a percent would be a derived number on a screen whose whole promise is that its numbers
// are the ledger's.
//
// CONSENT-GATED PII IS OPTIONAL AND NULLABLE. `CrmDonorOut.name`/`.email` and most of
// `CrmImportedContactOut` are `string | null | undefined` on the wire. Render them through
// `displayText` — NEVER `String(value)`, which prints the literal text "null" on a record screen
// reads as fact.

import type { BadgeProps } from '@givmo/design-system';
import { isGivmoError } from '@givmo/api-client';
import { isCrmUnavailable } from './crm-transport';
import type {
  CrmContactListOut,
  CrmContactOut,
  CrmContactSource,
  CrmDonorDetailOut,
  CrmDonorListOut,
  CrmDonorNoteOut,
  CrmDonorOut,
  CrmDoNotEmailReason,
  CrmGiftWindowUnit,
  CrmImportedAmount,
  CrmImportedContactListOut,
  CrmImportedContactOut,
  CrmSegmentCondition,
  CrmSegmentDefinition,
  CrmSegmentMembersOut,
  CrmSegmentOptionsOut,
  CrmTagCountOut,
  Section321DonorDonationOut,
} from './wire-types';

/** The Badge's tone union (the design-system doesn't re-export the tone type directly). */
type BadgeTone = NonNullable<BadgeProps['tone']>;

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/** A calendar date written `YYYY-MM-DD` (ASCII digits), or null. */
function calendarDate(value: unknown): string | null {
  return typeof value === 'string' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value) ? value : null;
}

/** An imported amount as the server writes one (a decimal string beside its ISO 4217 code), or null. */
function importedAmount(value: unknown): CrmImportedAmount | null {
  const row = record(value);
  if (row === null) return null;
  return typeof row.amount === 'string' &&
    /^[0-9]+(\.[0-9]{1,2})?$/.test(row.amount) &&
    typeof row.currency === 'string' &&
    /^[A-Z]{3}$/.test(row.currency)
    ? { amount: row.amount, currency: row.currency }
    : null;
}

/** A per-currency figure: each well-formed amount, a malformed one dropped, never shown wrong. */
function importedAmounts(value: unknown): CrmImportedAmount[] {
  return (Array.isArray(value) ? value : [])
    .map(importedAmount)
    .filter((item): item is CrmImportedAmount => item !== null);
}

function finiteNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

export function decodeCrmDonor(value: unknown): CrmDonorOut | null {
  const row = record(value);
  if (row === null) return null;
  const donorId = requiredString(row.donor_id);
  const total = requiredString(row.total_donated);
  if (donorId === null || total === null) return null;
  return {
    donor_id: donorId,
    name: optionalString(row.name),
    email: optionalString(row.email),
    total_donated: total,
    donation_count: finiteNumber(row.donation_count),
    first_donation_date: optionalString(row.first_donation_date),
    last_donation_date: optionalString(row.last_donation_date),
    recurring: row.recurring === true,
    tags: stringList(row.tags),
    do_not_email: row.do_not_email === true,
  };
}

function decodeCrmNote(value: unknown): CrmDonorNoteOut | null {
  const row = record(value);
  if (row === null) return null;
  const id = requiredString(row.id);
  const body = requiredString(row.body);
  if (id === null || body === null) return null;
  return {
    id,
    body,
    author_name: optionalString(row.author_name),
    created_at: optionalString(row.created_at),
    updated_at: optionalString(row.updated_at),
  };
}

function decodeDonation(value: unknown): Section321DonorDonationOut | null {
  const row = record(value);
  if (row === null) return null;
  const id = requiredString(row.id);
  const gross = requiredString(row.gross);
  const net = requiredString(row.net);
  const totalFee = requiredString(row.total_fee);
  if (id === null || gross === null || net === null || totalFee === null) return null;
  return {
    id,
    gross,
    net,
    total_fee: totalFee,
    date_made: optionalString(row.date_made),
    date_sent: optionalString(row.date_sent),
    sent: row.sent === true,
    designation: optionalString(row.designation),
    dedication_type: optionalString(row.dedication_type),
    dedication_name: optionalString(row.dedication_name),
    fundraiser_name: optionalString(row.fundraiser_name),
  };
}

function decodeImportedContact(value: unknown): CrmImportedContactOut | null {
  const row = record(value);
  if (row === null) return null;
  const id = requiredString(row.id);
  const source = requiredString(row.source);
  if (id === null || source === null) return null;
  return {
    id,
    display_name: optionalString(row.display_name),
    email: optionalString(row.email),
    phone: optionalString(row.phone),
    postal_address: optionalString(row.postal_address),
    tags: stringList(row.tags),
    notes: optionalString(row.notes),
    source,
    external_ref: optionalString(row.external_ref),
    created_at: optionalString(row.created_at),
    updated_at: optionalString(row.updated_at),
    do_not_email: row.do_not_email === true,
    last_gift_date: calendarDate(row.last_gift_date),
    first_gift_date: calendarDate(row.first_gift_date),
    last_gift_amount: importedAmount(row.last_gift_amount),
    lifetime_giving: importedAmounts(row.lifetime_giving),
    largest_gift: importedAmounts(row.largest_gift),
    do_not_email_reason: doNotEmailReason(row.do_not_email_reason),
    do_not_email_since: optionalString(row.do_not_email_since),
  };
}

const DO_NOT_EMAIL_REASONS: readonly CrmDoNotEmailReason[] = [
  'unsubscribed',
  'spam_report',
  'deceased',
  'bounced_hard',
  'imported_unsubscribe',
  'do_not_contact',
  'org_marked',
];

/** A reason this build does not know reads as null: the panel then says "Not emailed" alone. */
function doNotEmailReason(value: unknown): CrmDoNotEmailReason | null {
  return typeof value === 'string' && (DO_NOT_EMAIL_REASONS as readonly string[]).includes(value)
    ? (value as CrmDoNotEmailReason)
    : null;
}

/** One of the organization's own contacts (the contact panel's read); null when unreadable. */
export function decodeCrmImportedContact(value: unknown): CrmImportedContactOut | null {
  return decodeImportedContact(value);
}

/** Named wrapper decode. An unreadable consent bit fails closed; malformed rows are omitted. */
export function decodeCrmDonorList(value: unknown): CrmDonorListOut {
  const body = record(value);
  const consentAvailable = body?.consent_available === true;
  const rows = Array.isArray(body?.donors) ? body.donors : [];
  return {
    consent_available: consentAvailable,
    donors: consentAvailable
      ? rows.map(decodeCrmDonor).filter((row): row is CrmDonorOut => row !== null)
      : [],
    message: optionalString(body?.message),
  };
}

/** Named wrapper decode retaining the server's reachable pagination scalars. */
export function decodeCrmImportedContactList(value: unknown): CrmImportedContactListOut {
  const body = record(value);
  const rows = Array.isArray(body?.contacts) ? body.contacts : [];
  const contacts = rows
    .map(decodeImportedContact)
    .filter((row): row is CrmImportedContactOut => row !== null);
  return {
    contacts,
    total: finiteNumber(body?.total, contacts.length),
    limit: finiteNumber(body?.limit),
    offset: finiteNumber(body?.offset),
  };
}

/**
 * Decode one detail response. Missing or malformed `donor` returns null, which is a safe record state
 * rather than an object access crash. This is the CPA-UIX8(c) differential.
 */
export function decodeCrmDonorDetail(value: unknown): CrmDonorDetailOut | null {
  const body = record(value);
  if (body === null) return null;
  const donor = decodeCrmDonor(body.donor);
  if (donor === null) return null;
  const donations = (Array.isArray(body.donations) ? body.donations : [])
    .map(decodeDonation)
    .filter((row): row is Section321DonorDonationOut => row !== null);
  const notes = (Array.isArray(body.notes) ? body.notes : [])
    .map(decodeCrmNote)
    .filter((row): row is CrmDonorNoteOut => row !== null);
  return { donor, donations, notes };
}

/** Format a wire money STRING for display without a number conversion. */
export function displayMoney(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) return value;
  const sign = match[1] === '-' ? '-' : '';
  const integer = (match[2] ?? '0').replace(/^0+(?=\d)/, '');
  const fraction = (match[3] ?? '').slice(0, 2).padEnd(2, '0');
  let grouped = '';
  for (let index = 0; index < integer.length; index += 1) {
    if (index > 0 && (integer.length - index) % 3 === 0) grouped += ',';
    grouped += integer[index];
  }
  return `$${sign}${grouped}.${fraction}`;
}

/** A currency's sign as a U.S. reader sees it ("$", "£", "€", "CA$"), or its code when none. */
function currencySign(currency: string): string {
  try {
    const part = new Intl.NumberFormat('en-US', { style: 'currency', currency })
      .formatToParts(0)
      .find((item) => item.type === 'currency');
    return part?.value ?? `${currency} `;
  } catch {
    return `${currency} `;
  }
}

/**
 * Format an imported amount in the currency it was given in ("£40.00", "$1,500.00"), from its wire
 * string without a number conversion. Never shows a non-U.S. amount as dollars.
 */
export function displayImportedAmount(item: CrmImportedAmount): string {
  const formatted = displayMoney(item.amount);
  return formatted.startsWith('$')
    ? `${currencySign(item.currency)}${formatted.slice(1)}`
    : formatted;
}

/** Format an ISO timestamp as a stable UTC calendar date. */
export function displayDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Display an integer count without deriving a replacement value. */
export function displayCount(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '—' : String(value);
}

/**
 * Render an optional wire STRING (donor name/email, imported-contact fields) for display. Trims, and
 * falls back to an em dash when absent or blank. This exists so no caller reaches for `String(value)`
 * on a nullable field — that renders the literal text "null" into an organization-facing record.
 */
export function displayText(value: string | null | undefined, fallback = '—'): string {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : fallback;
}

/** Direction of a year-over-year comparison. `none` = nothing to compare against (or unreadable). */
export type YearTrend = 'up' | 'down' | 'flat' | 'none';

const DECIMAL_STRING = /^-?\d+(?:\.\d+)?$/;

/** Whether a sign-stripped decimal string is zero (`0`, `0.00`, `.0`, …). */
function isZeroMagnitude(magnitude: string): boolean {
  return /^0*(?:\.0*)?$/.test(magnitude);
}

/** Order two sign-stripped decimal strings digit-wise. No numeric conversion. */
function compareMagnitude(a: string, b: string): -1 | 0 | 1 {
  const [aWhole = '', aFraction = ''] = a.split('.');
  const [bWhole = '', bFraction = ''] = b.split('.');
  const aDigits = aWhole.replace(/^0+(?=\d)/, '');
  const bDigits = bWhole.replace(/^0+(?=\d)/, '');
  if (aDigits.length !== bDigits.length) return aDigits.length < bDigits.length ? -1 : 1;
  if (aDigits !== bDigits) return aDigits < bDigits ? -1 : 1;
  const width = Math.max(aFraction.length, bFraction.length);
  const aPadded = aFraction.padEnd(width, '0');
  const bPadded = bFraction.padEnd(width, '0');
  if (aPadded === bPadded) return 0;
  return aPadded < bPadded ? -1 : 1;
}

/**
 * Compare two money wire STRINGS as ordered values: `-1` (a below b), `0` (equal), `1` (a above b), or
 * `null` when either side is absent or is not a plain decimal (tolerant reader — an unreadable value
 * simply has no trend rather than crashing or being coerced).
 *
 * MONEY LAW: this produces an ORDERING, never a number. Nothing derived from it is ever displayed as a
 * value — only as a direction glyph.
 */
export function compareMoneyStrings(
  a: string | null | undefined,
  b: string | null | undefined,
): -1 | 0 | 1 | null {
  const left = a?.trim() ?? '';
  const right = b?.trim() ?? '';
  if (!DECIMAL_STRING.test(left) || !DECIMAL_STRING.test(right)) return null;

  const leftMagnitude = left.startsWith('-') ? left.slice(1) : left;
  const rightMagnitude = right.startsWith('-') ? right.slice(1) : right;
  // A signed zero (`-0.00`) is still zero; treat sign as meaningless there.
  const leftSign = isZeroMagnitude(leftMagnitude) ? 0 : left.startsWith('-') ? -1 : 1;
  const rightSign = isZeroMagnitude(rightMagnitude) ? 0 : right.startsWith('-') ? -1 : 1;
  if (leftSign !== rightSign) return leftSign < rightSign ? -1 : 1;
  if (leftSign === 0) return 0;

  const magnitude = compareMagnitude(leftMagnitude, rightMagnitude);
  if (magnitude === 0) return 0;
  // Below zero the ordering inverts: the larger magnitude is the smaller value.
  return leftSign > 0 ? magnitude : magnitude < 0 ? 1 : -1;
}

/** Year-over-year direction for a money wire string against the previous year's. */
export function moneyTrend(
  current: string | null | undefined,
  previous: string | null | undefined,
): YearTrend {
  const ordering = compareMoneyStrings(current, previous);
  if (ordering === null) return 'none';
  return ordering > 0 ? 'up' : ordering < 0 ? 'down' : 'flat';
}

/** Year-over-year direction for an integer count (already a number on the wire) against the previous. */
export function countTrend(
  current: number | null | undefined,
  previous: number | null | undefined,
): YearTrend {
  if (current == null || previous == null) return 'none';
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return 'none';
  if (current === previous) return 'flat';
  return current > previous ? 'up' : 'down';
}

/**
 * Badge tone for an imported-contact `source`. PURELY VISUAL — the human-facing text is always the
 * server's own token. A supplied record is never toned like a feed-sourced record.
 */
export function importedSourceTone(source: string | null | undefined): BadgeTone {
  return (source ?? '').trim().toLowerCase() === 'manual' ? 'neutral' : 'info';
}

/**
 * Whether an error means "this slice does not exist in this environment" — the backend's
 * The CRM module gate 404s the whole route so a dark slice is indistinguishable from a route that was
 * never deployed. Screens render "Not available yet" for this, never a crash.
 */
export function isSliceUnavailable(error: unknown): boolean {
  return isCrmUnavailable(error);
}

/**
 * D-CP-32 — whether a tag/note write was REFUSED terminally, so the UI must surface the server's own
 * message once and stop (no retry, no re-arm).
 *
 * The backend answers residual non-qualification with the same failure across subject states, so the
 * write path cannot become an oracle. That compatibility failure arrives as **400**; 404 is reserved
 * for the module gate. Both are terminal, as is a 409 duplicate.
 *
 * Consequently the UI offers tag and note controls for every listed subject and special-cases none.
 */
export function isTerminalWriteRefusal(error: unknown): boolean {
  if (!isGivmoError(error)) return false;
  return error.status === 400 || error.status === 404 || error.status === 409;
}

/**
 * Whether a write was refused because the organization holds no operative processing instrument
 * (a 409). This is the one refusal worth explaining in place because the host can supply an action.
 */
export function isTermsRequired(error: unknown): boolean {
  return isGivmoError(error) && error.status === 409;
}

/** The server's own message when there is one, else a caller-supplied fallback. Never invents copy. */
export function serverMessage(error: unknown, fallback: string): string {
  return isGivmoError(error) && error.message.trim().length > 0 ? error.message : fallback;
}

/** One row of feed activity: a server line or an organization-authored note. */
export type TimelineEntry =
  | { kind: 'donation'; at: string | null | undefined; donation: Section321DonorDonationOut }
  | { kind: 'note'; at: string | null | undefined; note: CrmDonorNoteOut };

/** Sort key for a timeline entry; unparseable/absent timestamps sort last (never crash a row). */
function timelineOrder(at: string | null | undefined): number {
  if (!at) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(at);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

/**
 * Interleave the server's donation lines and notes into one newest-first timeline.
 *
 * This is ORDERING ONLY — never derivation. Both sides are rendered exactly as the server returned
 * them; the client only decides which row is printed first.
 */
export function buildTimeline(
  donations: readonly Section321DonorDonationOut[],
  notes: readonly CrmDonorNoteOut[],
): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    ...donations.map(
      (donation): TimelineEntry => ({
        kind: 'donation',
        at: donation.date_made,
        donation,
      }),
    ),
    ...notes.map((note): TimelineEntry => ({ kind: 'note', at: note.created_at, note })),
  ];
  return entries.sort((a, b) => {
    const left = timelineOrder(a.at);
    const right = timelineOrder(b.at);
    // Compare rather than subtract: two undated rows are both -Infinity, and `-Inf - -Inf` is NaN —
    // an inconsistent comparator, whose row order is then engine-dependent. Equal ranks stay put.
    if (left === right) return 0;
    return left < right ? 1 : -1;
  });
}

// ── CRM-5: the whole list and segments ────────────────────────────────────────────────────────────

const SOURCES: readonly CrmContactSource[] = ['feed', 'import', 'manual'];

function isSource(value: unknown): value is CrmContactSource {
  return typeof value === 'string' && (SOURCES as readonly string[]).includes(value);
}

/** One contact row. A row without its id or source is omitted. */
export function decodeCrmContact(value: unknown): CrmContactOut | null {
  const row = record(value);
  if (row === null) return null;
  const contactId = requiredString(row.contact_id);
  const source = requiredString(row.source);
  if (contactId === null || source === null) return null;
  return {
    contact_id: contactId,
    source,
    donor_id: optionalString(row.donor_id),
    name: optionalString(row.name),
    email: optionalString(row.email),
    tags: stringList(row.tags),
    last_gift_at: optionalString(row.last_gift_at),
    last_gift_on: calendarDate(row.last_gift_on),
    do_not_email: row.do_not_email === true,
  };
}

/**
 * The date a contact's Last gift shows: a Givmo row's last shared gift, or the date an import
 * brought for one of the organization's own rows. Each row reads its own source's field alone.
 */
export function contactLastGift(contact: CrmContactOut): string | null {
  return contact.source === 'feed'
    ? (contact.last_gift_at ?? null)
    : (contact.last_gift_on ?? null);
}

/**
 * A list of contacts behind the consent bit. An unreadable bit fails closed: no Givmo row is kept,
 * whatever the body carries, while the organization's own rows are.
 */
function decodeGatedContacts(rows: unknown, consentAvailable: boolean): CrmContactOut[] {
  return (Array.isArray(rows) ? rows : [])
    .map(decodeCrmContact)
    .filter((row): row is CrmContactOut => row !== null)
    .filter((row) => consentAvailable || row.source !== 'feed');
}

export function decodeCrmContactList(value: unknown): CrmContactListOut {
  const body = record(value);
  const consentAvailable = body?.consent_available === true;
  const contacts = decodeGatedContacts(body?.contacts, consentAvailable);
  return {
    consent_available: consentAvailable,
    message: optionalString(body?.message),
    contacts,
    total: finiteNumber(body?.total, contacts.length),
    limit: finiteNumber(body?.limit),
    offset: finiteNumber(body?.offset),
  };
}

export function decodeCrmSegmentMembers(value: unknown): CrmSegmentMembersOut {
  const body = record(value);
  const consentAvailable = body?.consent_available === true;
  const members = decodeGatedContacts(body?.members, consentAvailable);
  return {
    consent_available: consentAvailable,
    message: optionalString(body?.message),
    members,
    total: finiteNumber(body?.total, members.length),
    limit: finiteNumber(body?.limit),
    offset: finiteNumber(body?.offset),
  };
}

/** The longest rolling window each unit takes (the backend's `MAX_WINDOW`: a century). */
export const GIFT_WINDOW_MAX: Record<CrmGiftWindowUnit, number> = {
  days: 36_525,
  months: 1_200,
  years: 100,
};

function decodeCondition(value: unknown): CrmSegmentCondition | null {
  const row = record(value);
  if (row === null) return null;
  const values = Array.isArray(row.values) ? row.values : null;
  switch (row.field) {
    case 'source':
      if ((row.op === 'is' || row.op === 'is_not') && values !== null && values.every(isSource)) {
        return { field: 'source', op: row.op, values: [...values] };
      }
      return null;
    case 'import_batch':
      if ((row.op === 'is' || row.op === 'is_not') && values !== null) {
        const ids = stringList(values);
        return ids.length === values.length
          ? { field: 'import_batch', op: row.op, values: ids }
          : null;
      }
      return null;
    case 'tag':
      if ((row.op === 'any' || row.op === 'all' || row.op === 'none') && values !== null) {
        const labels = stringList(values);
        return labels.length === values.length
          ? { field: 'tag', op: row.op, values: labels }
          : null;
      }
      return null;
    case 'relationship':
      if ((row.op === 'any' || row.op === 'none') && values !== null) {
        const types = stringList(values);
        return types.length === values.length
          ? { field: 'relationship', op: row.op, values: types }
          : null;
      }
      return null;
    case 'has_email':
      return row.op === 'is' && typeof row.value === 'boolean'
        ? { field: 'has_email', op: 'is', value: row.value }
        : null;
    case 'imported_last_gift_date': {
      const start = calendarDate(row.value);
      if (start === null) return null;
      if ((row.op === 'before' || row.op === 'after') && row.end === undefined) {
        return { field: 'imported_last_gift_date', op: row.op, value: start };
      }
      const end = calendarDate(row.end);
      return row.op === 'between' && end !== null && end >= start
        ? { field: 'imported_last_gift_date', op: 'between', value: start, end }
        : null;
    }
    case 'last_gift_date': {
      if (row.op === 'within') {
        const unit = row.unit;
        return row.value === undefined &&
          row.end === undefined &&
          typeof row.amount === 'number' &&
          Number.isInteger(row.amount) &&
          (unit === 'days' || unit === 'months' || unit === 'years') &&
          row.amount >= 1 &&
          row.amount <= GIFT_WINDOW_MAX[unit]
          ? { field: 'last_gift_date', op: 'within', amount: row.amount, unit }
          : null;
      }
      const start = calendarDate(row.value);
      if (start === null || row.amount !== undefined || row.unit !== undefined) return null;
      if ((row.op === 'before' || row.op === 'after') && row.end === undefined) {
        return { field: 'last_gift_date', op: row.op, value: start };
      }
      const end = calendarDate(row.end);
      return row.op === 'between' && end !== null && end >= start
        ? { field: 'last_gift_date', op: 'between', value: start, end }
        : null;
    }
    default:
      return null;
  }
}

/**
 * A stored definition, or null when ANY part of it is unreadable here (an unknown version, or a
 * condition this build does not know). Null is not "everyone": a screen must never re-send a
 * definition it could not read in full, because that would quietly change the segment.
 */
export function decodeCrmSegmentDefinition(value: unknown): CrmSegmentDefinition | null {
  const body = record(value);
  if (body === null || body.v !== 1 || !Array.isArray(body.all)) return null;
  const conditions = body.all.map(decodeCondition);
  if (conditions.some((condition) => condition === null)) return null;
  return { v: 1, all: conditions as CrmSegmentCondition[] };
}

/** A segment as this build can show it; `definition` is null when it cannot be read in full. */
export interface CrmSegmentView {
  id: string;
  name: string;
  definition: CrmSegmentDefinition | null;
  count: number | null;
  updated_at: string | null;
}

function decodeSegmentView(value: unknown, count: unknown): CrmSegmentView | null {
  const row = record(value);
  if (row === null) return null;
  const id = requiredString(row.id);
  const name = requiredString(row.name);
  if (id === null || name === null) return null;
  return {
    id,
    name,
    definition: decodeCrmSegmentDefinition(row.definition),
    count: typeof count === 'number' && Number.isFinite(count) ? count : null,
    updated_at: optionalString(row.updated_at) ?? optionalString(row.created_at),
  };
}

export interface CrmSegmentListView {
  consent_available: boolean;
  message: string | null;
  segments: CrmSegmentView[];
}

export function decodeCrmSegmentList(value: unknown): CrmSegmentListView {
  const body = record(value);
  const rows = Array.isArray(body?.segments) ? body.segments : [];
  return {
    consent_available: body?.consent_available === true,
    message: optionalString(body?.message),
    segments: rows
      .map((row) => decodeSegmentView(row, record(row)?.count))
      .filter((row): row is CrmSegmentView => row !== null),
  };
}

export interface CrmSegmentDetailView {
  consent_available: boolean;
  message: string | null;
  segment: CrmSegmentView;
  sample: CrmContactOut[];
}

/** One segment's read, or null when the body has no readable segment. */
export function decodeCrmSegmentDetail(value: unknown): CrmSegmentDetailView | null {
  const body = record(value);
  if (body === null) return null;
  const segment = decodeSegmentView(body.segment, body.count);
  if (segment === null) return null;
  const consentAvailable = body.consent_available === true;
  return {
    consent_available: consentAvailable,
    message: optionalString(body.message),
    segment,
    sample: decodeGatedContacts(body.sample, consentAvailable),
  };
}

/** The builder's value lists. Malformed tags are omitted. */
export function decodeCrmSegmentOptions(value: unknown): CrmSegmentOptionsOut {
  const body = record(value);
  const tags = (Array.isArray(body?.tags) ? body.tags : [])
    .map((item): CrmTagCountOut | null => {
      const row = record(item);
      const label = requiredString(row?.label);
      return label === null ? null : { label, count: finiteNumber(row?.count) };
    })
    .filter((row): row is CrmTagCountOut => row !== null);
  return { tags, relationship_types: stringList(body?.relationship_types) };
}

/** Format an integer count with grouping ("2,471"). Display only; never a derived value. */
export function displayGroupedCount(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '—' : value.toLocaleString('en-US');
}
